import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, PlayCircle, ArrowRight, Loader2, Edit3, Save, X, Plus, Trash2,
  Image as ImageIcon, Film, Upload, RotateCcw, ExternalLink,
} from 'lucide-react';
import { toEmbedUrl, detectMediaKind } from '../../utils/embedUrl';
import { Link } from 'react-router-dom';

// =====================================================================
// Onboarding Welcome Dashboard
// Shown above the orientation modules. Admin-editable hero, intro video,
// graphic slot, walkthrough steps, and sign-off. Renders for new hires
// AND tenured staff (read-only for non-admins).
// =====================================================================
export default function WelcomePanel({ api, user, isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/onboarding/welcome');
      setCfg(data);
    } catch (e) {
      // soft-fail; the panel just stays hidden
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return null;

  if (editing) {
    return (
      <WelcomeEditor
        api={api}
        initial={cfg}
        onClose={() => setEditing(false)}
        onSaved={(next) => { setCfg(next); setEditing(false); toast.success('Welcome page updated'); }}
      />
    );
  }

  return (
    <WelcomeView cfg={cfg} user={user} isAdmin={isAdmin} onEdit={() => setEditing(true)} />
  );
}

// ---------------------------------------------------------------------
// View — what every staff member sees
// ---------------------------------------------------------------------
function WelcomeView({ cfg, user, isAdmin, onEdit }) {
  const firstName = (user?.name || '').split(' ')[0] || 'team';
  const heroStyle = cfg.hero_photo_b64
    ? { backgroundImage: `linear-gradient(135deg, rgba(45,27,78,0.82), rgba(107,63,160,0.55)), url(${cfg.hero_photo_b64})` }
    : { background: 'linear-gradient(135deg, #2D1B4E 0%, #6B3FA0 100%)' };

  const videoEmbed = cfg.intro_video_url ? toEmbedUrl(cfg.intro_video_url) : '';
  const videoKind = cfg.intro_video_url ? detectMediaKind(cfg.intro_video_url) : '';

  return (
    <div className="welcome-panel mb-8" data-testid="welcome-panel">
      {/* HERO */}
      <div
        className="relative rounded-2xl p-8 md:p-12 text-white overflow-hidden shadow-lg"
        style={{ ...heroStyle, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="welcome-hero">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={onEdit}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-md px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 border border-white/30 transition"
              data-testid="welcome-edit-btn">
              <Edit3 className="h-3.5 w-3.5" /> Customize
            </button>
          )}
        </div>
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-wider mb-4">
            <Sparkles className="h-3 w-3" /> Hi {firstName}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-3" data-testid="welcome-hero-title">
            {cfg.hero_title}
          </h2>
          <p className="text-base md:text-lg text-white/90 leading-relaxed" data-testid="welcome-hero-subtitle">
            {cfg.hero_subtitle}
          </p>
        </div>
      </div>

      {/* VIDEO + GRAPHIC */}
      {(videoEmbed || cfg.graphic_b64) && (
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {videoEmbed && (
            <div className="bg-white border border-[var(--rule)] rounded-xl overflow-hidden shadow-sm" data-testid="welcome-video">
              <div className="aspect-video bg-black">
                {videoKind === 'mp4' ? (
                  <video src={videoEmbed} controls className="w-full h-full" />
                ) : (
                  <iframe
                    src={videoEmbed}
                    title="Welcome video"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
              {cfg.intro_video_caption && (
                <div className="p-3 text-xs text-[var(--ink-fade)] flex items-center gap-1.5">
                  <PlayCircle className="h-3.5 w-3.5" /> {cfg.intro_video_caption}
                </div>
              )}
            </div>
          )}
          {cfg.graphic_b64 && (
            <div className="bg-white border border-[var(--rule)] rounded-xl overflow-hidden shadow-sm" data-testid="welcome-graphic">
              <img src={cfg.graphic_b64} alt={cfg.graphic_caption || 'Welcome graphic'} className="w-full h-full object-cover aspect-video" />
              {cfg.graphic_caption && (
                <div className="p-3 text-xs text-[var(--ink-fade)] flex items-center gap-1.5">
                  <ImageIcon className="h-3.5 w-3.5" /> {cfg.graphic_caption}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* WALKTHROUGH STEPS */}
      {(cfg.walkthrough_steps || []).length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold text-[var(--ink)] mb-3 flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-[var(--accent)]" /> Your quick tour
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cfg.walkthrough_steps.map((step, i) => (
              <WalkthroughCard key={step.id || i} step={step} index={i + 1} />
            ))}
          </div>
        </div>
      )}

      {/* SIGN-OFF */}
      {(cfg.signoff_name || cfg.signoff_message) && (
        <div className="mt-6 bg-gradient-to-r from-[#F8F5FB] to-white border-l-4 border-[var(--accent)] rounded-r-xl p-5" data-testid="welcome-signoff">
          {cfg.signoff_message && (
            <p className="text-sm text-[var(--ink)] italic leading-relaxed mb-3">&ldquo;{cfg.signoff_message}&rdquo;</p>
          )}
          {cfg.signoff_name && (
            <div className="text-xs">
              <span className="font-bold text-[var(--ink)]">{cfg.signoff_name}</span>
              {cfg.signoff_title && <span className="text-[var(--ink-fade)]"> · {cfg.signoff_title}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WalkthroughCard({ step, index }) {
  const hasLink = !!step.link_url;
  const isInternal = hasLink && step.link_url.startsWith('/');
  const inner = (
    <div className="h-full bg-white border border-[var(--rule)] rounded-xl p-4 hover:border-[var(--accent)] hover:shadow-md transition group" data-testid={`walkthrough-step-${index}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-6 w-6 rounded-full bg-[var(--accent)] text-white text-[11px] font-bold flex items-center justify-center">{index}</span>
        <h4 className="text-sm font-bold text-[var(--ink)] flex-1">{step.title}</h4>
      </div>
      {step.image_b64 && (
        <img src={step.image_b64} alt={step.title} className="w-full aspect-video object-cover rounded-md mb-2 border border-[var(--rule)]" />
      )}
      {step.body && <p className="text-xs text-[var(--ink-soft)] leading-relaxed mb-2">{step.body}</p>}
      {hasLink && (
        <div className="text-xs font-semibold text-[var(--accent)] flex items-center gap-1 group-hover:gap-2 transition-all">
          {step.link_label || 'Open'}
          {isInternal ? <ArrowRight className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
        </div>
      )}
    </div>
  );
  if (hasLink && isInternal) return <Link to={step.link_url} className="block">{inner}</Link>;
  if (hasLink) return <a href={step.link_url} target="_blank" rel="noreferrer" className="block">{inner}</a>;
  return inner;
}

// ---------------------------------------------------------------------
// Editor — admin-only
// ---------------------------------------------------------------------
function WelcomeEditor({ api, initial, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => ({
    ...initial,
    walkthrough_steps: initial.walkthrough_steps || [],
  }));
  const [saving, setSaving] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify({ ...initial, walkthrough_steps: initial.walkthrough_steps || [] }), [draft, initial]);

  const requestClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const fileToB64 = (file) => new Promise((resolve, reject) => {
    if (!file) return resolve('');
    if (file.size > 6 * 1024 * 1024) return reject(new Error('Image must be under 6 MB'));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickHero = async (e) => {
    try {
      const b64 = await fileToB64(e.target.files?.[0]);
      if (b64) setField('hero_photo_b64', b64);
    } catch (err) { toast.error(err.message); }
  };
  const onPickGraphic = async (e) => {
    try {
      const b64 = await fileToB64(e.target.files?.[0]);
      if (b64) setField('graphic_b64', b64);
    } catch (err) { toast.error(err.message); }
  };

  const addStep = () => setDraft(d => ({
    ...d,
    walkthrough_steps: [...(d.walkthrough_steps || []), { id: '', title: '', body: '', image_b64: '', link_url: '', link_label: '' }],
  }));
  const updateStep = (i, k, v) => setDraft(d => {
    const next = [...(d.walkthrough_steps || [])];
    next[i] = { ...next[i], [k]: v };
    return { ...d, walkthrough_steps: next };
  });
  const removeStep = (i) => setDraft(d => ({
    ...d,
    walkthrough_steps: (d.walkthrough_steps || []).filter((_, idx) => idx !== i),
  }));
  const onPickStepImage = async (i, file) => {
    try {
      const b64 = await fileToB64(file);
      if (b64) updateStep(i, 'image_b64', b64);
    } catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/onboarding/welcome', draft);
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Restore the welcome page to factory defaults? Your current photo, video, and walkthrough steps will be cleared.')) return;
    setSaving(true);
    try {
      const { data } = await api.post('/onboarding/welcome/reset');
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="welcome-editor mb-8 bg-white border border-[var(--rule)] rounded-xl p-5 shadow-sm" data-testid="welcome-editor">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--rule)]">
        <div>
          <h2 className="text-lg font-bold text-[var(--ink)] flex items-center gap-2">
            <Edit3 className="h-4 w-4 text-[var(--accent)]" /> Customize Welcome Page
            {isDirty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4D6] text-[#8A6500] border border-[#E6C76A] font-bold uppercase tracking-wider" data-testid="welcome-dirty-badge">Unsaved</span>}
          </h2>
          <p className="text-xs text-[var(--ink-fade)]">Edit the hero, intro video, graphic, and walkthrough steps new staff see when they sign in.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetDefaults} disabled={saving} className="text-xs text-[var(--ink-fade)] hover:text-[var(--accent)] flex items-center gap-1" data-testid="welcome-reset-btn">
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
          <button onClick={requestClose} disabled={saving} className="text-xs px-3 py-1.5 rounded-md border border-[var(--rule)] hover:bg-[var(--paper)]" data-testid="welcome-cancel-btn">Cancel</button>
          <button onClick={save} disabled={saving} className="text-xs px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[#5A2F8F] text-white font-semibold flex items-center gap-1.5" data-testid="welcome-save-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
        </div>
      </div>

      {/* Hero block */}
      <Section title="Hero banner">
        <Field label="Heading">
          <input value={draft.hero_title || ''} onChange={e => setField('hero_title', e.target.value)} className="welcome-input" placeholder="Welcome to The Next Step." data-testid="welcome-input-hero-title" />
        </Field>
        <Field label="Sub-text">
          <textarea value={draft.hero_subtitle || ''} onChange={e => setField('hero_subtitle', e.target.value)} className="welcome-input min-h-[80px]" placeholder="One or two sentences setting the tone." data-testid="welcome-input-hero-subtitle" />
        </Field>
        <Field label="Background photo (optional)">
          <PhotoSlot value={draft.hero_photo_b64} onPick={onPickHero} onClear={() => setField('hero_photo_b64', '')} testid="welcome-hero-photo" />
        </Field>
      </Section>

      {/* Video */}
      <Section title="Intro video (optional)">
        <Field label="Video URL — YouTube, Loom, Vimeo, or .mp4">
          <input value={draft.intro_video_url || ''} onChange={e => setField('intro_video_url', e.target.value)} className="welcome-input" placeholder="https://www.loom.com/share/…" data-testid="welcome-input-video-url" />
        </Field>
        <Field label="Caption (shown under the player)">
          <input value={draft.intro_video_caption || ''} onChange={e => setField('intro_video_caption', e.target.value)} className="welcome-input" placeholder="A quick hello from the team" data-testid="welcome-input-video-caption" />
        </Field>
      </Section>

      {/* Graphic */}
      <Section title="Graphic slot (optional)">
        <Field label="Image — mission poster, org chart, anything visual">
          <PhotoSlot value={draft.graphic_b64} onPick={onPickGraphic} onClear={() => setField('graphic_b64', '')} testid="welcome-graphic-photo" />
        </Field>
        <Field label="Caption">
          <input value={draft.graphic_caption || ''} onChange={e => setField('graphic_caption', e.target.value)} className="welcome-input" placeholder="Our mission, values, and what we stand for" data-testid="welcome-input-graphic-caption" />
        </Field>
      </Section>

      {/* Walkthrough steps */}
      <Section title="Walkthrough steps">
        <p className="text-xs text-[var(--ink-fade)] -mt-2 mb-3">Small cards that point new staff at the parts of TNSwork they&apos;ll use most. Re-order with the buttons. Use internal links (start with <code>/</code>) or external URLs.</p>
        <div className="space-y-3">
          {(draft.walkthrough_steps || []).map((step, i) => (
            <StepEditor
              key={i}
              step={step}
              index={i}
              total={(draft.walkthrough_steps || []).length}
              onChange={(k, v) => updateStep(i, k, v)}
              onRemove={() => removeStep(i)}
              onPickImage={(file) => onPickStepImage(i, file)}
            />
          ))}
        </div>
        <button onClick={addStep} className="mt-3 text-xs text-[var(--accent)] flex items-center gap-1 font-semibold hover:underline" data-testid="welcome-add-step-btn">
          <Plus className="h-3 w-3" /> Add step
        </button>
      </Section>

      {/* Sign-off */}
      <Section title="Sign-off">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name">
            <input value={draft.signoff_name || ''} onChange={e => setField('signoff_name', e.target.value)} className="welcome-input" placeholder="Josh Sutton" data-testid="welcome-input-signoff-name" />
          </Field>
          <Field label="Title">
            <input value={draft.signoff_title || ''} onChange={e => setField('signoff_title', e.target.value)} className="welcome-input" placeholder="Founder & CEO" data-testid="welcome-input-signoff-title" />
          </Field>
        </div>
        <Field label="Personal message">
          <textarea value={draft.signoff_message || ''} onChange={e => setField('signoff_message', e.target.value)} className="welcome-input min-h-[70px]" placeholder="A short, personal note from leadership." data-testid="welcome-input-signoff-message" />
        </Field>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 pb-5 border-b border-[var(--rule)] last:border-b-0 last:pb-0">
      <h3 className="text-sm font-bold text-[var(--ink)] mb-3 uppercase tracking-wide">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--ink-soft)] mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function PhotoSlot({ value, onPick, onClear, testid }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-40 aspect-video bg-[var(--paper)] border border-[var(--rule)] rounded-md overflow-hidden flex items-center justify-center text-[var(--ink-fade)]" data-testid={`${testid}-preview`}>
        {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6" />}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs px-3 py-1.5 rounded-md border border-[var(--rule)] hover:border-[var(--accent)] hover:text-[var(--accent)] cursor-pointer flex items-center gap-1.5 inline-flex w-fit" data-testid={`${testid}-upload`}>
          <Upload className="h-3 w-3" /> {value ? 'Replace' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={onPick} />
        </label>
        {value && (
          <button onClick={onClear} className="text-xs text-[var(--ink-fade)] hover:text-red-600 flex items-center gap-1 w-fit" data-testid={`${testid}-clear`}>
            <X className="h-3 w-3" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function StepEditor({ step, index, total, onChange, onRemove, onPickImage }) {
  return (
    <div className="border border-[var(--rule)] rounded-lg p-3 bg-[var(--paper)]" data-testid={`step-editor-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[var(--ink-soft)]">Step {index + 1} of {total}</span>
        <button onClick={onRemove} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid={`step-remove-${index}`}>
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={step.title || ''} onChange={e => onChange('title', e.target.value)} className="welcome-input" placeholder="Step title (e.g. Your Schedule)" data-testid={`step-title-${index}`} />
        <input value={step.link_url || ''} onChange={e => onChange('link_url', e.target.value)} className="welcome-input" placeholder="Link URL (e.g. /my-schedule)" data-testid={`step-link-${index}`} />
      </div>
      <textarea value={step.body || ''} onChange={e => onChange('body', e.target.value)} className="welcome-input mt-2 min-h-[60px]" placeholder="Short description (1-2 sentences)" data-testid={`step-body-${index}`} />
      <div className="grid sm:grid-cols-2 gap-2 mt-2 items-end">
        <input value={step.link_label || ''} onChange={e => onChange('link_label', e.target.value)} className="welcome-input" placeholder="Link button label (e.g. Open My Schedule)" data-testid={`step-link-label-${index}`} />
        <div>
          <label className="text-[11px] px-2 py-1 rounded border border-[var(--rule)] hover:border-[var(--accent)] cursor-pointer flex items-center gap-1 w-fit" data-testid={`step-image-upload-${index}`}>
            <Upload className="h-3 w-3" /> {step.image_b64 ? 'Replace image' : 'Add image'}
            <input type="file" accept="image/*" className="hidden" onChange={e => onPickImage(e.target.files?.[0])} />
          </label>
        </div>
      </div>
      {step.image_b64 && (
        <div className="mt-2 flex items-center gap-2">
          <img src={step.image_b64} alt="" className="w-20 aspect-video object-cover rounded border border-[var(--rule)]" />
          <button onClick={() => onChange('image_b64', '')} className="text-[11px] text-[var(--ink-fade)] hover:text-red-600" data-testid={`step-image-clear-${index}`}>Remove image</button>
        </div>
      )}
    </div>
  );
}
